import { useCallback, useEffect, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import { describeFailure } from '@/lib/describe-failure';
import {
  createQualityMetricRun,
  deleteQualityMetricRun,
  getQualityMetricRuns,
  reconcileQualityMetricSchedulers,
  triggerQualityMetricRun,
  updateQualityMetricRun,
  type QualityMetricReconcile,
  type QualityMetricRunPayload,
  type QualityMetricRunView,
} from '@/api/quality-metric.api.private';
import { QUALITY_METRIC_ERROR_CODES } from 'shared/constants/quality-metric.constants';

/**
 * The organization's scheduled pulls for one source, with the writes that
 * change them.
 *
 * Runs live on the configuration document but are written through their own
 * endpoints, and every write also returns what the API did in Cloud Scheduler.
 * That second half is why this is a hook rather than local state: a write can
 * succeed and still leave the schedulers out of sync, and the banner for that
 * has to survive the list refresh that follows.
 *
 * @param args.source - The pull source; `mpog` is the only one today. The
 *   organization is the session's: mount this only once the organization page
 *   has switched into it.
 * @returns The list (`runs`, `managed`, `timeZone`), its read state
 *   (`loading`, `error`, `reload`), the outstanding `reconcileErrors`, the
 *   in-flight markers `busyRunId` and `resyncing`, and the writes `saveRun`,
 *   `removeRun`, `triggerRun` and `resync`. `saveRun` takes a run id or `null`
 *   to create, and resolves to an error message for the caller to render, or
 *   `null` once stored — every other write toasts its own failure.
 * @example
 * const runs = useRuns({ source: SOURCE });
 * const message = await runs.saveRun(null, { schedule: '0 0 * * 4', lookback: '3mo' });
 */
export function useRuns({ source }: { source: string }) {
  const { toast } = useToast();

  const [runs, setRuns] = useState<QualityMetricRunView[]>([]);
  const [managed, setManaged] = useState(true);
  const [timeZone, setTimeZone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reconcileErrors, setReconcileErrors] = useState<QualityMetricReconcile['errors']>([]);
  const [busyRunId, setBusyRunId] = useState<string | null>(null);
  const [resyncing, setResyncing] = useState(false);

  const load = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      setLoading(true);
      setError(null);

      const res = await getQualityMetricRuns({ source });
      if (isCancelled()) return;

      if (res.error) {
        setError(describeFailure(res));
        setLoading(false);
        return;
      }

      setRuns(res.data.runs ?? []);
      setManaged(res.data.managed);
      setTimeZone(res.data.timeZone);
      setLoading(false);
    },
    [source],
  );

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  /**
   * A Scheduler failure never fails the write, so the errors are carried out of
   * the response and kept on screen until the next clean write or re-sync —
   * otherwise the only trace of a half-applied change is a toast that has
   * already gone.
   */
  const absorbReconcile = (reconcile: QualityMetricReconcile | undefined) => {
    setReconcileErrors(reconcile?.errors ?? []);
    if (reconcile && !reconcile.managed) setManaged(false);
  };

  /** Resolves to an error message for the editor to show, or `null` on success. */
  const saveRun = async (
    runId: string | null,
    data: QualityMetricRunPayload,
  ): Promise<string | null> => {
    const res = runId
      ? await updateQualityMetricRun({ source, runId, data })
      : await createQualityMetricRun({ source, data });

    if (res.error) {
      if (res.code === QUALITY_METRIC_ERROR_CODES.RUN_WINDOW_TAKEN) {
        return 'Another run of this organization already pulls that window, and pulling the same data twice is not allowed. Change the window, or edit the run that already has it.';
      }
      return describeFailure(res);
    }

    absorbReconcile(res.data.reconcile);
    await load();
    toast({ title: runId ? 'Run updated' : 'Run added' });
    return null;
  };

  const removeRun = async (run: QualityMetricRunView) => {
    setBusyRunId(run._id);
    const res = await deleteQualityMetricRun({ source, runId: run._id });
    setBusyRunId(null);

    if (res.error) {
      toast({
        title: 'Failed to delete the run',
        description: describeFailure(res),
        variant: 'destructive',
      });
      return;
    }

    absorbReconcile(res.data.reconcile);
    await load();
    toast({ title: 'Run deleted' });
  };

  const triggerRun = async (run: QualityMetricRunView) => {
    setBusyRunId(run._id);
    const res = await triggerQualityMetricRun({ source, runId: run._id });
    setBusyRunId(null);

    if (res.error) {
      const description =
        res.code === QUALITY_METRIC_ERROR_CODES.SCHEDULER_NOT_MANAGED
          ? 'This environment does not run schedulers, so there is no job to tick. The run is stored and will tick wherever schedulers are managed.'
          : res.code === QUALITY_METRIC_ERROR_CODES.SCHEDULER_NOT_FOUND
            ? 'The Scheduler job for this run is missing. Re-sync the schedulers, then try again.'
            : describeFailure(res);
      toast({ title: 'Could not run it now', description, variant: 'destructive' });
      return;
    }

    // 202 only asks for the pull — the records land whenever the sync service
    // gets through them, so this deliberately does not claim it finished.
    toast({
      title: 'Pull requested',
      description: 'The sync service was asked to pull now. Records land once it has run.',
    });
  };

  const resync = async () => {
    setResyncing(true);
    const res = await reconcileQualityMetricSchedulers({ source });
    setResyncing(false);

    if (res.error) {
      toast({
        title: 'Re-sync failed',
        description: describeFailure(res),
        variant: 'destructive',
      });
      return;
    }

    absorbReconcile(res.data);
    await load();
    toast({
      title: res.data.errors?.length ? 'Re-synced, with errors' : 'Schedulers re-synced',
    });
  };

  return {
    runs,
    managed,
    timeZone,
    loading,
    error,
    reconcileErrors,
    busyRunId,
    resyncing,
    reload: load,
    saveRun,
    removeRun,
    triggerRun,
    resync,
  };
}

export type RunsState = ReturnType<typeof useRuns>;
